import { useState, useRef, useEffect, Fragment, lazy, Suspense } from 'react'
import './index.css'
const Antigravity = lazy(() => import('./Antigravity'))
import {
  ChevronDown,
  Linkedin,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  Send,
} from 'lucide-react'

/* ─── Navbar ─── */
function Navbar() {
  const [open, setOpen] = useState(false)
  const links = [
    { label: 'About', href: '#about' },
    { label: 'Experience', href: '#stats' },
    { label: 'Contact', href: '#contact' },
  ]

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [open])

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-gray-100">
        <nav className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="#" className="flex items-center">
            <img
              src="/logo.png"
              alt="AgilITy Logo"
              width="132"
              height="72"
              className="h-[72px] w-auto"
            />
          </a>

          {/* Desktop links */}
          <ul className="hidden md:flex gap-8 px-6">
            {links.map((l) => (
              <li key={l.label}>
                <a
                  href={l.href}
                  className="text-sm font-medium text-gray-600 hover:text-black transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-black pr-6 focus:outline-none"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Mobile menu (Full Screen Overlay) */}
      <div
        className={`fixed inset-0 bg-white z-40 md:hidden flex flex-col pt-[73px] transition-all duration-300 ease-in-out ${
          open
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        {/* Centered menu links */}
        <div className="flex-1 flex flex-col items-center justify-center gap-10">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-3xl font-light uppercase tracking-widest text-gray-700 hover:text-black transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Footer inside mobile menu */}
        <div className="pb-12 text-center text-xs text-gray-500 tracking-wider">
          © 2026 AgilITy. All rights reserved.
        </div>
      </div>
    </>
  )
}

/* ─── Typewriter Title ─── */
function TypewriterTitle() {
  const fullText = 'Hi, we are AgilITy'
  // Stop points where deletion can pause (character indices)
  const stopPoints = [
    0,                        // full erase
    3,                        // "Hi," 
    'Hi, we are '.length,     // "Hi, we are "
  ]

  const [displayed, setDisplayed] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const deleteTarget = useRef(0)

  useEffect(() => {
    let timeout

    if (!isDeleting && displayed.length < fullText.length) {
      // Typing forward
      timeout = setTimeout(() => {
        setDisplayed(fullText.slice(0, displayed.length + 1))
      }, 80)
    } else if (!isDeleting && displayed.length === fullText.length) {
      // Fully typed — wait 36 seconds, then pick a random stop point
      timeout = setTimeout(() => {
        deleteTarget.current =
          stopPoints[Math.floor(Math.random() * stopPoints.length)]
        setIsDeleting(true)
      }, 36000)
    } else if (isDeleting && displayed.length > deleteTarget.current) {
      // Deleting back to the chosen stop point
      timeout = setTimeout(() => {
        setDisplayed(fullText.slice(0, displayed.length - 1))
      }, 40)
    } else if (isDeleting) {
      // Reached stop point — pause briefly, then start re-typing
      timeout = setTimeout(() => setIsDeleting(false), 500)
    }

    return () => clearTimeout(timeout)
  }, [displayed, isDeleting])

  // Split into "Hi, we are " and "Sonar Byte"
  const prefix = 'Hi, we are '
  const showPrefix = displayed.slice(0, prefix.length)
  const showBrand = displayed.slice(prefix.length)

  return (
    <h1 className="text-5xl md:text-7xl font-extrabold text-black leading-tight tracking-tight min-h-[120px] md:min-h-[80px]">
      {showPrefix}
      {showBrand && (
        <span className="bg-gradient-to-r from-black via-gray-700 to-gray-500 bg-clip-text text-transparent">
          {showBrand.split('').map((char, index) => {
            if (index === 4 || index === 5) {
              return (
                <span 
                  key={index} 
                  className="text-[#17325F]" 
                  style={{ WebkitTextFillColor: '#17325F' }}
                >
                  {char}
                </span>
              );
            }
            return <span key={index}>{char}</span>;
          })}
        </span>
      )}
      <span className="inline-block w-[3px] h-[0.85em] bg-black ml-1 align-middle animate-blink" />
    </h1>
  )
}

/* ─── Hero Section ─── */
function Hero() {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <section
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 pt-24 pb-16 overflow-hidden"
    >
      {/* Background Animation */}
      <div
        className={`absolute inset-0 z-0 transition-opacity duration-700 ease-in-out ${
          isHovered ? 'opacity-40' : 'opacity-0'
        }`}
      >
        <Suspense fallback={null}>
          <Antigravity
            count={300}
            magnetRadius={6}
            ringRadius={7}
            waveSpeed={1.8}
            waveAmplitude={1}
            particleSize={1.5}
            lerpSpeed={0.06}
            color="#1C375B"
            autoAnimate
            particleVariance={1}
            rotationSpeed={0}
            depthFactor={1}
            pulseSpeed={3}
            particleShape="capsule"
            fieldStrength={10}
          />
        </Suspense>
      </div>

      {/* Hero content */}
      <div className="relative z-10 flex flex-col items-center pointer-events-none">
        <TypewriterTitle />

        <p className="max-w-2xl mt-8 text-base md:text-lg text-gray-500 leading-relaxed pointer-events-none">
          Σχεδιάζουμε και υλοποιούμε καινοτόμες τεχνολογικές λύσεις που αναβαθμίζουν
          τη λειτουργία κάθε επιχείρησης. Μέσα από τον στρατηγικό ψηφιακό
          μετασχηματισμό και τη χρήση τεχνολογιών αιχμής, μετατρέπουμε τις
          τεχνολογικές σας προκλήσεις σε πραγματικό ανταγωνιστικό πλεονέκτημα.
        </p>

        {/* Social icons */}
        <div className="flex gap-4 mt-8 pointer-events-auto">
          <a
            href="https://www.linkedin.com/company/we-are-agility/?viewAsMember=true"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 rounded-full border border-gray-200 text-gray-500 hover:text-black hover:border-black transition-all hover:shadow-md bg-white/50 backdrop-blur-sm"
            aria-label="LinkedIn"
          >
            <Linkedin size={20} />
          </a>
          <a
            href="mailto:info@agility.gr"
            className="p-3 rounded-full border border-gray-200 text-gray-500 hover:text-black hover:border-black transition-all hover:shadow-md bg-white/50 backdrop-blur-sm"
            aria-label="Email"
          >
            <Mail size={20} />
          </a>
        </div>

        {/* Animated arrow */}
        <div className="mt-16 animate-bounce-slow pointer-events-auto">
          <ChevronDown size={32} className="text-gray-500" />
        </div>
      </div>
    </section>
  )
}

/* ─── About & Services ─── */
function About() {
  const services = [
    {
      title: 'ΜΗΧΑΝΟΓΡΑΦΗΣΗ ΕΠΙΧΕΙΡΗΣΕΩΝ – CLOUD ERP',
      desc: 'Μηχανογραφήστε την εταιρεία σας εύκολα και γρήγορα με τις κορυφαίες εφαρμογές PRISMA Win της Megasoft. Διαχειριστείτε πλήρως τις πωλήσεις, τις αγορές και την αποθήκη σας μέσω Cloud, εξασφαλίζοντας ασφάλεια δεδομένων, ευελιξία και άμεση πρόσβαση από παντού.',
      bgColor: 'bg-[#F5F8FC]',
    },
    {
      title: 'ΨΗΦΙΑΚΗ ΤΗΛΕΦΩΝΙΑ',
      desc: 'Εγκατάσταση και παραμετροποίηση σύγχρονων ψηφιακών τηλεφωνικών κέντρων VoIP (βασισμένα σε Asterisk). Αναβαθμίστε την εταιρική σας επικοινωνία με προηγμένες λειτουργίες δρομολόγησης, καταγραφή κλήσεων και πλήρη υποστήριξη απομακρυσμένης εργασίας, διασφαλίζοντας μέγιστη αξιοπιστία και ευελιξία.',
      bgColor: 'bg-[#EBF1F9]',
    },
    {
      title: 'ΚΑΤΑΣΚΕΥΗ ΙΣΤΟΣΕΛΙΔΩΝ',
      desc: 'Σχεδιασμός και ανάπτυξη σύγχρονων, γρήγορων και ασφαλών ιστοσελίδων και e-shop, πλήρως προσαρμοσμένων στις ανάγκες σας. Δημιουργούμε ψηφιακές εμπειρίες που αναδεικνύουν την ταυτότητά σας και προσφέρουν άψογη πλοήγηση σε κάθε συσκευή.',
      bgColor: 'bg-[#E1EAF4]',
    },
    {
      title: 'ΥΠΟΣΤΗΡΙΞΗ ΔΙΚΤΥΩΝ',
      desc: 'Ολοκληρωμένη παροχή τεχνικής υποστήριξης, εγκατάστασης, διαχείρισης και συντήρησης εταιρικών δικτύων. Σχεδιάζουμε αξιόπιστες και γρήγορες δικτυακές υποδομές, διασφαλίζοντας την αδιάλειπτη και ασφαλή λειτουργία της επιχείρησής σας.',
      bgColor: 'bg-[#D7E2F0]',
    },
  ]

  const bgColors = ['#F5F8FC', '#EBF1F9', '#E1EAF4', '#D7E2F0']

  return (
    <section id="about" className="px-6 py-24 md:py-32 max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        
        {/* Left Column (Sticky) */}
        <div className="sticky top-24 z-0 space-y-6">
          <div>
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-500">
              Who we are
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-black mt-3 uppercase tracking-[0.2em] leading-tight">
              About Us
            </h2>
          </div>
          <p className="text-base md:text-lg text-gray-500 leading-relaxed">
            Με περισσότερα από 26 χρόνια εμπειρίας στον κλάδο της Πληροφορικής,
            παρέχουμε ολοκληρωμένες λύσεις τεχνολογίας και ψηφιακού μετασχηματισμού
            σε επιχειρήσεις κάθε μεγέθους.
          </p>
        </div>

        {/* Right Column (Stacking Cards) */}
        <div className="cards-column w-full relative z-10">
          {services.map((s, index) => (
            <div 
              key={index}
              className="sticky-card-wrapper"
              style={{ '--wrapper-top': `${index * 400}px` }}
            >
              <div
                className="sticky-card p-6 md:p-8 rounded-[24px] border border-slate-200/60 shadow-xl hover:shadow-2xl transition-shadow duration-300"
                style={{
                  '--card-top': `${100 + index * 80}px`,
                  '--card-margin': `${(services.length - 1 - index) * 80}px`,
                  zIndex: index + 1,
                  backgroundColor: bgColors[index]
                }}
              >
                <div className="w-full">
                  <hr className="border-slate-300/80 mb-4" />
                  <h3 className="text-xs font-semibold tracking-widest text-slate-600 mb-4">
                    {s.title}
                  </h3>
                  <p className="text-base md:text-lg text-slate-800 leading-relaxed mt-4">
                    {s.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  )
}

/* ─── Animated Counter Hook ─── */
function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!start) return
    let startTime = null
    let frame

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setCount(Math.floor(eased * target))
      if (progress < 1) {
        frame = requestAnimationFrame(animate)
      }
    }

    frame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frame)
  }, [target, duration, start])

  return count
}

/* ─── Stats Bar ─── */
function Stats() {
  const [visible, setVisible] = useState(false)
  const sectionRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    if (sectionRef.current) observer.observe(sectionRef.current)
    return () => observer.disconnect()
  }, [])

  const count1 = useCountUp(107, 2000, visible)
  const count2 = useCountUp(26, 2000, visible)
  const count3 = useCountUp(32295, 2000, visible)

  const stats = [
    { value: `${count1}+`, label: 'Συνεργατες' },
    { value: `${count2}+`, label: 'Χρονια Εμπειριας' },
    { value: `${count3.toLocaleString('el-GR')}+`, label: 'Αιτηματα Υποστηριξης' },
  ]

  return (
    <section
      ref={sectionRef}
      id="stats"
      className="bg-white text-black py-16 md:py-20 border-y border-gray-100"
    >
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-4xl md:text-5xl font-light text-black">
              {s.value}
            </p>
            <p className="mt-2 text-sm text-gray-500 font-medium uppercase tracking-wider">
              {s.label}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

/* ─── Contact Section ─── */
function Contact() {
  const [status, setStatus] = useState('') // 'submitting', 'success', 'error', ''
  
  const info = [
    {
      icon: <Mail size={20} />,
      label: 'Email',
      value: 'info@agility.gr',
      href: null,
    },
    {
      icon: <Phone size={20} />,
      label: 'Phone',
      value: '(+30) 6906912862',
      href: null,
    },
    {
      icon: <MapPin size={20} />,
      label: 'Location',
      value: 'Πειραιάς, Ελλάδα',
      href: null,
    },
  ]

  const handleSubmit = async (e) => {
    e.preventDefault()
    const form = e.target
    const data = new FormData(form)
    
    setStatus('submitting')
    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: data,
        headers: {
          'Accept': 'application/json'
        }
      })
      if (response.ok) {
        setStatus('success')
        form.reset()
      } else {
        setStatus('error')
      }
    } catch (error) {
      setStatus('error')
    }
  }

  return (
    <section id="contact" className="relative z-10 bg-white w-full">
      <div className="px-6 py-24 md:py-32 max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold tracking-widest uppercase text-gray-500">
            Reach out
          </span>
          <h2 className="text-3xl md:text-4xl font-light text-black mt-3 uppercase tracking-[0.2em]">
            Get In Touch
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          {/* Info column */}
          <div className="space-y-8">
            {info.map((item) => (
              <div key={item.label} className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-black shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {item.label}
                  </p>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-black font-medium hover:underline"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-black font-medium">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Form column */}
          <div>
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] p-8 text-center bg-gray-50 rounded-[24px] border border-gray-100">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-black mb-2">Σας ευχαριστούμε για το αίτημά σας!</h3>
                <p className="text-gray-500 max-w-[280px]">Το μήνυμά σας ελήφθη επιτυχώς. Θα επικοινωνήσουμε μαζί σας το συντομότερο δυνατό.</p>
                <button 
                  onClick={() => setStatus('')}
                  className="mt-8 text-sm text-gray-500 hover:text-black underline underline-offset-4 transition-colors"
                >
                  Αποστολή νέου μηνύματος
                </button>
              </div>
            ) : (
              <form
                action="https://formspree.io/f/maqppljj"
                method="POST"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input
                    type="text"
                    name="name"
                    placeholder="Name"
                    required
                    className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition-all"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email"
                    required
                    className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition-all"
                  />
                </div>
                <input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition-all"
                />
                <textarea
                  name="message"
                  placeholder="Message"
                  rows={5}
                  required
                  className="w-full px-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-sm text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition-all resize-none"
                />
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center gap-2 bg-black text-white px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-gray-800 transition-all hover:scale-105 active:scale-95 shadow-lg shadow-black/10 disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed"
                >
                  <Send size={16} />
                  {status === 'submitting' ? 'Αποστολή...' : 'Στείλτε αίτημα'}
                </button>
                {status === 'error' && (
                  <p className="text-red-500 text-sm mt-2 font-medium">Υπήρξε ένα πρόβλημα κατά την αποστολή. Παρακαλώ δοκιμάστε ξανά.</p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Footer ─── */
function Footer() {
  const StyledAgility = () => (
    <span className="bg-gradient-to-r from-black via-gray-700 to-gray-500 bg-clip-text text-transparent">
      Agil
      <span className="text-[#17325F]" style={{ WebkitTextFillColor: '#17325F' }}>IT</span>
      y
    </span>
  );

  return (
    <footer className="relative z-10 bg-white text-black border-t border-gray-100">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <h3 className="text-lg font-bold mb-4">
          <StyledAgility />
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-6 max-w-md">
          Led by passionate people about leveraging technology to solve
          complex business challenges. Building innovative solutions for
          digital transformation.
        </p>
        <div className="flex gap-3">
          <a
            href="https://www.linkedin.com/company/we-are-agility/?viewAsMember=true"
            target="_blank"
            rel="noopener noreferrer"
            className="p-2.5 rounded-full border border-gray-200 text-gray-500 hover:text-black hover:border-black transition-colors"
            aria-label="LinkedIn"
          >
            <Linkedin size={16} />
          </a>

          <a
            href="mailto:info@agility.gr"
            className="p-2.5 rounded-full border border-gray-200 text-gray-500 hover:text-black hover:border-black transition-colors"
            aria-label="Email"
          >
            <Mail size={16} />
          </a>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center">
          <p className="text-xs text-gray-500">
            © 2026 AgilITy. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}

/* ─── App ─── */
function App() {
  useEffect(() => {
    const loadAnalytics = () => {
      if (window.analyticsLoaded) return
      window.analyticsLoaded = true

      // Load Google Tag Manager script
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-P4PDKKTXZ9'
      document.head.appendChild(script)

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || []
      function gtag() {
        window.dataLayer.push(arguments)
      }
      window.gtag = gtag
      gtag('js', new Date())
      gtag('config', 'G-P4PDKKTXZ9')
      gtag('config', 'AW-18117395184')
    }

    // Load after 3 seconds, or when user scrolls/clicks
    const timer = setTimeout(loadAnalytics, 3000)

    const triggerEvents = ['click', 'mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll']
    triggerEvents.forEach((event) => {
      window.addEventListener(event, loadAnalytics, { once: true, passive: true })
    })

    return () => {
      clearTimeout(timer)
      triggerEvents.forEach((event) => {
        window.removeEventListener(event, loadAnalytics)
      })
    }
  }, [])

  return (
    <div className="bg-white text-black">
      <Navbar />
      <main id="main-content">
        <Hero />
        <About />
        <Stats />
        <Contact />
      </main>
      <Footer />
    </div>
  )
}

export default App
